// Map controls (2026-10-25, owner: "the camera teleports", "zoom goes crazy from low angles", "the trackpad is hyper"):
// the numbers every camera gesture shares, so a grab, a wheel notch, a trackpad swipe, a pinch and a double-click all
// move the camera at a rate set by how high it is, never by how far away the thing under the cursor happens to be.
// As in the map apps people know (Google Maps, Apple Maps, Mapbox GL, Cesium):
//   - a pivot (orbit, zoom, double-click) is the point under the cursor, but never further along the cursor ray than
//     pivotCap(height above the ground): a ray grazing the horizon dollies a step set by the height, not by the
//     kilometres to the hill it meets
//   - a grabbed point further than missDist(height) counts as a miss (the sky)
//   - a pan follows the grabbed point exactly up to a reach, then at a constant rate (planeRange), and its depth stops
//     GRAZE above the horizon; each move is clamped to MOVE_K x the start's metres per pixel; a release glide is at most
//     1.5 heights a second
//   - the wheel: a trackpad is told from a mouse once per gesture (wheelDevice): two fingers swipe = pan, pinch
//     (ctrl + wheel) = zoom 1:1 with the fingers, a mouse notch = an eased zoom step; a swipe is softened and snaps
//     to forward or sideways while it stays near one (createSwipe)
// Pure (no three.js): tests/unit/runtime/navmath.test.mjs. Users: camerabounds.js (grab pan, grab orbit), main.js
// (the wheel, the shared pick, double-click).

const { abs, atan2, cos, exp, hypot, log, max, min, sign, sin, sqrt, tan } = Math;
export const HAG_FLOOR = 10; // m: the least height above the ground the rates are worked out for (Film flies 1.5 m up)
export const MOVE_K = 3; // a pan move: at most 3 x the drag start's metres per pixel, per pixel moved
export const GRAZE = 3 * Math.PI / 180; // a ray within 3 degrees of the drag plane moves no further in depth (a grab there turns the view)
export const PLANE_K = 0.3; // the drag plane: at least 30 % of the height under the camera

/** Height of the camera above the ground under it, floored (m). */
export const heightAbove = (camY, groundY) => max(HAG_FLOOR, camY - groundY || 0); // (no ground: NaN -> the floor)
/** The farthest a pivot may be along the cursor ray (m): 3 heights, at least 30 m. (Numbers inline: the engine budget.) */
export const pivotCap = (hag) => max(30, 3 * hag);
/** Beyond this a hit is a miss (m): 20 heights, at least 200 m. */
export const missDist = (hag) => max(200, 20 * hag);

/** (dx, dz) shortened to at most `cap` m. */
export function clampStep(dx, dz, cap) {
  const L = hypot(dx, dz);
  if (!(L > cap) || L < 1e-9) return [dx, dz];
  const k = cap / L;
  return [dx * k, dz * k];
}

/**
 * Horizontal distance along the ray's heading where a ray `a` rad below the horizontal meets a plane `H` m under the
 * camera: exact (H / tan a) out to the reach `R`, past it linear in the angle at the reach's rate (no dead zone, no
 * fling: the grabbed point keeps moving at a steady pace however close the cursor comes to the horizon) and no further
 * than GRAZE above the horizon (the depth stops; a sideways move still turns). Continuous, with its slope, at the reach.
 */
export function planeRange(a, H, R) {
  const a0 = max(atan2(H, max(R, 1e-6)), GRAZE);
  if (a >= a0) return H / tan(a);
  const s = sin(a0);
  return H / tan(a0) + (a0 - max(a, GRAZE)) * (H / (s * s));
}

/**
 * The wheel's pace (m): a notch moves the camera a share of this along the cursor ray. The distance to the surface seen
 * there (`hit` m on: a building, the ground; not a tree), but at least 2 heights (owner, 2026-10-25: "I need to scroll
 * like 100 times to get closer": a share of a shrinking distance never arrives) and at most pivotCap (never a far
 * hill's kilometres). zoomIn: a zoom-in step for a log zoom `lg` < 0, at most one height and never closer than 3 m to
 * the surface (<= 0: no step).
 */
export const zoomRef = (hit, hag) => min(max(hit, 2 * hag), pivotCap(hag));
export const zoomIn = (lg, hit, hag) => min(-Math.expm1(lg) * zoomRef(hit, hag), hag, hit - 3);

/** A release glide's speed (m/s): the finger's screen speed at the drag's metres per pixel, at most 1.5 heights a second. */
export const glideSpeed = (pxPerSec, mpp, hag) => min(pxPerSec * mpp, 1.5 * hag);

/** Orbit radians per pixel: a full turn per screen height (as MapControls), calmer for a lens narrower than the map's own. */
export const orbitRate = (h, fov, fovRef, speed = 1) => ((2 * Math.PI) / max(1, h)) * speed * min(1, fov / max(1e-6, fovRef || fov));
/** Turning the view in place (a grab at the horizon, Film's seat): the picture follows the pointer, radians per pixel. */
export const turnRate = (fovDeg, h) => (fovDeg * Math.PI) / 180 / max(1, h);

// ---- Film's free look (owner 2026-10-25: "change the angle freely up and down, even looking at the sky, without
// some sort of a lookup mode"; "if the perspective goes to the top, I cannot anymore change it back")
/** Film's right-drag: radians per pixel, the picture following the pointer, but at least 45 degrees a screen (a long lens). */
export const lookRate = (fovDeg, h) => turnRate(max(fovDeg, 45), h);
/**
 * The view direction [x, y, z] turned where the camera stands: `yaw` rad about the vertical (as a rotation about +y),
 * `down` rad towards the ground, the tilt kept in [lo, hi] rad above the horizontal (one already outside may only
 * come back). Straight down or up there is no heading to tilt along: `head` [x, z] (the picture's top) is used, so the
 * view always tilts back out of a top-down shot. The length is kept.
 */
export function freeLook([x, y, z], yaw, down, lo, hi, head = [0, -1]) {
  const L = hypot(x, y, z) || 1, l = hypot(x, z), p = atan2(y, l);
  const a = (l > 1e-7 * L ? atan2(x, z) : atan2(head[0], head[1])) + yaw, t = min(max(p - down, min(lo, p)), max(hi, p));
  return [sin(a) * cos(t) * L, sin(t) * L, cos(a) * cos(t) * L];
}
/** Film's drone move: metres per pixel at a height above the ground (the ground a pixel covers looking straight down). */
export const flyRate = (hag, fovDeg, h) => (2 * hag * tan((fovDeg * Math.PI) / 360)) / max(1, h);

// ---- the wheel ----------------------------------------------------------------------------------------------------
export const WHEEL = { line: 40, page: 400, mouse: 0.0021, notchMax: 200, pinch: 0.01, maxLog: Math.LN2, quiet: 220 };
const MAC_MOUSE = 4.000244140625; // Chrome on macOS: a mouse wheel's pixel deltas are multiples of this (Mapbox GL's test)

/**
 * 'mouse' or 'trackpad', from one wheel event (the first of a gesture). A mouse: line or page mode, Chrome's macOS
 * multiples of 4.000244140625, or a pixel delta of 40+ (Chrome / Edge on Windows: 100 a notch, more with display
 * scaling; fractional with the page zoomed: 90.9 at 110 %). A trackpad: a sideways delta (not Shift: Chrome turns
 * Shift + a mouse wheel sideways) or a small one (macOS and Windows precision trackpads send 1-15 px per event, ~16 ms apart).
 */
export function wheelDevice(e) {
  if (e.deltaMode) return 'mouse';
  const x = e.deltaX || 0, y = e.deltaY || 0, d = abs(y) >= abs(x) ? y : x;
  if (d !== 0 && d % MAC_MOUSE === 0) return 'mouse';
  if (x !== 0 && y !== 0 && !e.shiftKey) return 'trackpad';
  return abs(d) >= 40 ? 'mouse' : 'trackpad';
}

/**
 * One wheel gesture = one device: decided at its first event and kept until the wheel has been quiet for `quiet` ms
 * (macOS momentum events keep the gesture going, so a swipe's tail pans on and never turns into a zoom).
 * Returns (e, now) -> 'zoom' (a mouse notch: an eased zoom), 'pan' (two fingers swipe) or 'pinch' (ctrl + wheel on a
 * trackpad: Chrome and Firefox send a pinch so; zoom applied directly).
 */
export function createWheelSense({ quiet = WHEEL.quiet } = {}) {
  let device = null, last = -Infinity;
  return (e, now) => {
    if (!(now - last <= quiet)) device = null;
    last = now;
    device ||= wheelDevice(e);
    return device === 'mouse' ? 'zoom' : e.ctrlKey ? 'pinch' : 'pan';
  };
}

/** A wheel event's pixels (line and page modes turned into pixels); a mouse with Shift (sideways in Chrome) counts its deltaX. */
export function wheelPx(e) {
  const d = e.deltaY || (e.shiftKey ? e.deltaX : 0) || 0;
  return e.deltaMode === 1 ? d * WHEEL.line : e.deltaMode === 2 ? d * WHEEL.page : d;
}
/** The zoom (natural log of the distance factor, + = out) of a mouse notch: eased by the caller. */
export const notchLog = (px) => sign(px) * min(abs(px), WHEEL.notchMax) * WHEEL.mouse;
/** The zoom of a pinch event (Chrome: deltaY = -100 ln(scale), so 0.01 follows the fingers 1:1), at most ln 2. */
export const pinchLog = (px) => max(-WHEEL.maxLog, min(WHEEL.maxLog, px * WHEEL.pinch));

// ---- a trackpad's two-finger swipe (owner, 2026-10-25: "extremely sensitive even with the trackpad set low", a guest:
// "a fine move should lock straight forward or sideways, a circle should move freely") ---------------------------------
// macOS hands the page scroll pixels already accelerated (the trackpad's tracking speed does not change them) and keeps
// sending them after the fingers lift (momentum): taken 1:1, a quick swipe threw the map screens away. So a swipe moves
// the picture 0.6 of its pixels, softly capped at 1400 px/s (tanh: slow moves all but untouched, fast ones level off).
// Axis lock (as a scroll view's directional lock): the first 6 px pick a rail, forward/back or sideways, when the move
// is within atan(1 / 2) (~27 degrees) of it, else free; on a rail the sideways drift (leaky, 0.3 s) is dropped until it
// passes 24 px plus 0.3 of the move along the rail (a circle, a deliberate diagonal), then the swipe is free to its
// end. A new gesture after WHEEL.quiet ms decides again. Pixels in, pixels out (the caller turns them into metres at
// one rate for both axes: camerabounds.js nudge). (Numbers inline: the engine budget.)
/** Returns (dx, dy, now ms) -> [dx, dy], the picture's move for one swipe event (0, 0 while the rail is undecided). */
export function createSwipe() {
  let last = -Infinity, rail = 0, hx = 0, hy = 0, off = 0, along = 0; // rail: 0 deciding, 1 forward/back, 2 sideways, 3 free; h: held while deciding
  return (dx, dy, now) => {
    const dt = now - last;
    if (!(dt <= WHEEL.quiet)) rail = hx = hy = off = along = 0;
    last = now;
    if (!rail) {
      hx += dx; hy += dy;
      if (hypot(hx, hy) < 6) return [0, 0];
      rail = abs(hy) >= 2 * abs(hx) ? 1 : abs(hx) >= 2 * abs(hy) ? 2 : 3;
      dx = hx; dy = hy; // the held pixels, nothing lost
    } else if (rail < 3) {
      const f = exp(-dt / 300);
      off = off * f + (rail < 2 ? dx : dy); along = along * f + abs(rail < 2 ? dy : dx);
      if (abs(off) > 24 + 0.3 * along) rail = 3; // from this event on: no jump
    }
    if (rail === 1) dx = 0;
    if (rail === 2) dy = 0;
    const L = hypot(dx, dy) * 0.6, cap = 1.4 * min(max(dt, 4), 33); // px: 1400 px/s over the event's 4-33 ms
    return L > 1e-9 ? [dx, dy].map((d) => d * 0.6 * cap * Math.tanh(L / cap) / L) : [0, 0];
  };
}

// ---- two fingers on a touch screen (owner, 2026-10-25: "the tilt often doesn't start", "turning always also zooms") ----
// As Mapbox GL's touch handlers (Google Maps and Apple Maps feel the same): a finger has moved past 2 px (from where it
// landed); while only one has, the other gets 100 ms to follow (a thumb lags a frame or two); then both moved mostly up
// or down, the same way, with the fingers side by side = a tilt (exclusive, applied from the landing: nothing is lost),
// anything else = the map (pan with the midpoint, zoom, twist). Zoom and twist are each switched on at their own
// threshold, counted from the landing: twist past 25 px along the circle (7-20 degrees), zoom past 7 % of spread plus
// the twist so far (rad: a twist under way makes a wobbling spread wait); once one is on, the other needs 28 % (zoom)
// or 20 degrees (twist). Each applies from the moment it switched on (no jump).
const wrap = (d) => atan2(sin(d), cos(d));
const vert = (x, y) => abs(x) <= abs(y);
/**
 * One sample of the two fingers a, b ({x, y} px) at t ms, once a frame. `s`: the gesture's state, `{}` when the second
 * finger lands (s.l: the gesture once decided); s.p = 0 rebases (the next sample is a new start for the positions; a
 * decided gesture keeps its kind).
 * Returns 0 while undecided, else the gesture: 1 = tilt, 2 = the map; then s.m = [x0, y0, x, y] (the midpoint's move),
 * s.k (the distance factor of the zoom: < 1 = closer) and s.da (the twist, rad).
 */
export function twoFingers(s, a, b, t) {
  const d = max(12, hypot(b.x - a.x, b.y - a.y)), g = atan2(b.y - a.y, b.x - a.x);
  if (!s.p) { s.d = d; s.g = g; s.p = [a, b]; if (!s.l) { s.p0 = s.p; s.L = s.th = 0; s.dm = d; } return 0; }
  const [A, B] = s.p, [A0, B0] = s.p0, dl = log(d / s.d), da = wrap(g - s.g);
  s.L += dl; s.th += da; s.dm = min(s.dm, d); s.d = d; s.g = g;
  if (!s.l) {
    const ax = a.x - A0.x, ay = a.y - A0.y, bx = b.x - B0.x, by = b.y - B0.y, ma = hypot(ax, ay) >= 2, mb = hypot(bx, by) >= 2;
    if (ma && mb) s.l = vert(ax, ay) && vert(bx, by) && ay * by > 0 && !vert(B0.x - A0.x, B0.y - A0.y) ? 1 : 2;
    else if (ma || mb) { s.t1 ??= t; if (t - s.t1 > 100) s.l = 2; }
    if (!s.l) return 0;
  }
  s.m = [(A.x + B.x) / 2, (A.y + B.y) / 2, (a.x + b.x) / 2, (a.y + b.y) / 2];
  s.p = [a, b];
  if (s.l > 1) {
    s.z ||= abs(s.L) > (s.w ? 0.25 : 0.07 + abs(s.th));
    s.w ||= abs(s.th) > (s.z ? 0.35 : min(0.35, max(0.12, 50 / s.dm)));
  }
  s.k = s.z ? exp(-dl) : 1;
  s.da = s.w ? da : 0;
  return s.l;
}

// ---- trees as pick targets (no per-frame cost: the list is made at the first pick, a ray reads it on a press or a
// new wheel point: ~1 ms for 125 000 trees) --------------------------------------------------------------------------------
/** Distance along the unit ray (o, d) to a vertical cylinder (axis cx, cz; y0..y1; radius r), or Infinity. */
export function rayCylinder(o, d, cx, cz, y0, y1, r) {
  const ox = o.x - cx, oz = o.z - cz, a = d.x * d.x + d.z * d.z;
  let best = Infinity;
  if (a > 1e-12) {
    const b = ox * d.x + oz * d.z, c = ox * ox + oz * oz - r * r, disc = b * b - a * c;
    if (disc >= 0) {
      const t = (-b - sqrt(disc)) / a;
      if (t >= 0) { const y = o.y + d.y * t; if (y >= y0 && y <= y1) best = t; }
    }
  }
  if (abs(d.y) > 1e-9) { // the caps
    for (let k = 0; k < 2; k++) {
      const t = ((k ? y1 : y0) - o.y) / d.y;
      if (t >= 0 && t < best) { const x = ox + d.x * t, z = oz + d.z * t; if (x * x + z * z <= r * r) best = t; }
    }
  }
  return best;
}

/**
 * The trees as cylinders where the crown is seen: forEach(fn(x, z, h, r, species)) lists them, groundAt(x, z) gives
 * their foot (looked up only for a tree near the ray). A cylinder runs from the crown's start (CROWN_FROM: a pine's bare
 * trunk is not caught) to 90 % of the height (the thin tip is not either), radius `crown` x the crown radius (a cone's
 * mean). ray(o, d, far) is the distance to the first tree along the unit ray, or Infinity. A tree behind, off the
 * ray's line or past `far` costs a few multiplications.
 */
const CROWN_FROM = [0.55, 0.1, 0.3]; // share of the height where the crown starts: pine, spruce, birch (vegetation.js SPECIES and its models)
export function createTreeList(forEach, groundAt, crown = 0.6) {
  const a = [];
  forEach((x, z, h, r, s) => { if (h > 0.5) a.push(x, z, h, max(0.4, (r || h * 0.2) * crown), CROWN_FROM[s] ?? 0.3); });
  const T = Float32Array.from(a), G = new Float32Array(T.length / 5).fill(NaN);
  return {
    ray(o, d, far = Infinity) {
      const a2 = d.x * d.x + d.z * d.z, fh = min(far, 1e6) * sqrt(a2); // fh: how far the ray goes across the ground
      let best = Infinity;
      for (let i = 0; i < T.length; i += 5) {
        const ox = T[i] - o.x, oz = T[i + 1] - o.z, r = T[i + 3], cr = ox * d.z - oz * d.x;
        if (ox * d.x + oz * d.z < -r || ox * ox + oz * oz > (fh + r) ** 2 || cr * cr > r * r * max(a2, 1e-12)) continue; // behind, too far, off the line
        let g = G[i / 5];
        if (g !== g) g = G[i / 5] = groundAt(T[i], T[i + 1]);
        const t = rayCylinder(o, d, T[i], T[i + 1], g + T[i + 2] * T[i + 4], g + T[i + 2] * 0.9, r);
        if (t < best) best = t;
      }
      return best <= far ? best : Infinity;
    },
  };
}
